import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { FileText, Plus, Eye, ArrowRight, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  submitted: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  in_review: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  denied: "bg-red-500/10 text-red-400 border-red-500/20",
  completed: "bg-teal-500/10 text-teal-400 border-teal-500/20",
};

const statusIcons = {
  submitted: Clock,
  in_review: Eye,
  approved: CheckCircle,
  denied: XCircle,
  completed: CheckCircle,
};

const arcoTypeLabels = {
  access: "Acceso",
  rectification: "Rectificacion",
  cancellation: "Cancelacion",
  opposition: "Oposicion",
};

export default function Privacy() {
  const [tab, setTab] = useState("arco");
  const [arcoRequests, setArcoRequests] = useState([]);
  const [notices, setNotices] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewArco, setShowNewArco] = useState(false);
  const [showNewNotice, setShowNewNotice] = useState(false);
  const [showArcoDetail, setShowArcoDetail] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [arco, ntc, cns] = await Promise.all([
        axios.get(`${API}/privacy/arco`, { withCredentials: true }),
        axios.get(`${API}/privacy/notices`, { withCredentials: true }),
        axios.get(`${API}/privacy/consents`, { withCredentials: true }),
      ]);
      setArcoRequests(arco.data.items || []);
      setNotices(ntc.data.items || []);
      setConsents(cns.data.items || []);
    } catch {
      toast.error("Error cargando datos de privacidad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <Layout>
      <div data-testid="privacy-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Privacidad & ARCO</h1>
            <p className="text-slate-400 mt-1">Gestion LFPDPPP — Avisos, Consentimiento y Solicitudes ARCO</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900 border border-slate-800 mb-6">
            <TabsTrigger value="arco" data-testid="tab-arco">Solicitudes ARCO</TabsTrigger>
            <TabsTrigger value="notices" data-testid="tab-notices">Avisos de Privacidad</TabsTrigger>
            <TabsTrigger value="consents" data-testid="tab-consents">Consentimientos</TabsTrigger>
          </TabsList>

          {/* ARCO Tab */}
          <TabsContent value="arco">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowNewArco(true)} data-testid="new-arco-btn">
                <Plus size={16} className="mr-2" /> Nueva Solicitud ARCO
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : arcoRequests.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
                <AlertTriangle className="mx-auto mb-3 text-slate-600" size={36} />
                <p className="text-slate-400">No hay solicitudes ARCO registradas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {arcoRequests.map((r) => {
                  const StatusIcon = statusIcons[r.status] || Clock;
                  return (
                    <div key={r.arco_id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between" data-testid={`arco-${r.arco_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
                          <StatusIcon size={18} className="text-teal-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {arcoTypeLabels[r.request_type] || r.request_type} — {r.data_subject_name || "N/A"}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {r.arco_id} | {new Date(r.created_at).toLocaleDateString("es-MX")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={statusColors[r.status]} data-testid={`arco-status-${r.arco_id}`}>
                          {r.status}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => setShowArcoDetail(r)} data-testid={`arco-view-${r.arco_id}`}>
                          <Eye size={16} />
                        </Button>
                        {r.status !== "completed" && r.status !== "denied" && (
                          <Button variant="outline" size="sm" onClick={() => setShowStatusUpdate(r)} data-testid={`arco-update-${r.arco_id}`}>
                            <ArrowRight size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Notices Tab */}
          <TabsContent value="notices">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowNewNotice(true)} data-testid="new-notice-btn">
                <Plus size={16} className="mr-2" /> Nuevo Aviso
              </Button>
            </div>
            {notices.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
                <FileText className="mx-auto mb-3 text-slate-600" size={36} />
                <p className="text-slate-400">No hay avisos de privacidad</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notices.map((n) => (
                  <div key={n.notice_id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between" data-testid={`notice-${n.notice_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                        <FileText size={18} className="text-violet-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Version {n.version} | {new Date(n.created_at).toLocaleDateString("es-MX")}
                        </p>
                      </div>
                    </div>
                    <Badge className={n.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-slate-700/50 text-slate-400"}>
                      {n.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Consents Tab */}
          <TabsContent value="consents">
            {consents.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
                <CheckCircle className="mx-auto mb-3 text-slate-600" size={36} />
                <p className="text-slate-400">No hay consentimientos registrados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {consents.map((c) => (
                  <div key={c.consent_id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between" data-testid={`consent-${c.consent_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle size={18} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {c.consent_type} — Cliente: {c.customer_id || "N/A"}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {c.consent_id} | {c.acceptance_method} | {new Date(c.created_at).toLocaleDateString("es-MX")}
                        </p>
                      </div>
                    </div>
                    <Badge className={c.granted ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"}>
                      {c.granted ? "Otorgado" : "Denegado"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New ARCO Dialog */}
        <NewArcoDialog open={showNewArco} onClose={() => setShowNewArco(false)} onCreated={fetchAll} />

        {/* New Notice Dialog */}
        <NewNoticeDialog open={showNewNotice} onClose={() => setShowNewNotice(false)} onCreated={fetchAll} />

        {/* ARCO Detail Dialog */}
        {showArcoDetail && (
          <ArcoDetailDialog arco={showArcoDetail} onClose={() => setShowArcoDetail(null)} />
        )}

        {/* Status Update Dialog */}
        {showStatusUpdate && (
          <StatusUpdateDialog arco={showStatusUpdate} onClose={() => setShowStatusUpdate(null)} onUpdated={fetchAll} />
        )}
      </div>
    </Layout>
  );
}

function NewArcoDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ request_type: "", data_subject_name: "", data_subject_email: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.request_type || !form.data_subject_name) {
      toast.error("Tipo y nombre del titular son requeridos");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/privacy/arco`, form, { withCredentials: true });
      toast.success("Solicitud ARCO creada");
      onClose();
      onCreated();
      setForm({ request_type: "", data_subject_name: "", data_subject_email: "", description: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="new-arco-dialog">
        <DialogHeader>
          <DialogTitle>Nueva Solicitud ARCO</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Tipo de Derecho</Label>
            <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="arco-type-select">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="access">Acceso</SelectItem>
                <SelectItem value="rectification">Rectificacion</SelectItem>
                <SelectItem value="cancellation">Cancelacion</SelectItem>
                <SelectItem value="opposition">Oposicion</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre del Titular</Label>
            <Input className="bg-slate-800 border-slate-700" value={form.data_subject_name}
              onChange={(e) => setForm({ ...form, data_subject_name: e.target.value })}
              data-testid="arco-subject-name" />
          </div>
          <div>
            <Label>Email del Titular</Label>
            <Input className="bg-slate-800 border-slate-700" value={form.data_subject_email}
              onChange={(e) => setForm({ ...form, data_subject_email: e.target.value })}
              data-testid="arco-subject-email" />
          </div>
          <div>
            <Label>Descripcion</Label>
            <Textarea className="bg-slate-800 border-slate-700" rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="arco-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="arco-submit-btn">
            {submitting ? "Enviando..." : "Crear Solicitud"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewNoticeDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", content: "", summary: "", data_controller: "", arco_contact: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.content) { toast.error("Contenido requerido"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/privacy/notices`, form, { withCredentials: true });
      toast.success("Aviso de privacidad creado");
      onClose();
      onCreated();
      setForm({ title: "", content: "", summary: "", data_controller: "", arco_contact: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear aviso");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl" data-testid="new-notice-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo Aviso de Privacidad</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titulo</Label>
            <Input className="bg-slate-800 border-slate-700" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="notice-title" />
          </div>
          <div>
            <Label>Responsable de Datos</Label>
            <Input className="bg-slate-800 border-slate-700" value={form.data_controller}
              onChange={(e) => setForm({ ...form, data_controller: e.target.value })}
              data-testid="notice-controller" />
          </div>
          <div>
            <Label>Contacto ARCO</Label>
            <Input className="bg-slate-800 border-slate-700" value={form.arco_contact}
              onChange={(e) => setForm({ ...form, arco_contact: e.target.value })}
              data-testid="notice-arco-contact" />
          </div>
          <div>
            <Label>Resumen</Label>
            <Textarea className="bg-slate-800 border-slate-700" rows={2} value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              data-testid="notice-summary" />
          </div>
          <div>
            <Label>Contenido Completo</Label>
            <Textarea className="bg-slate-800 border-slate-700" rows={6} value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="notice-content" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="notice-submit-btn">
            {submitting ? "Creando..." : "Crear Aviso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArcoDetailDialog({ arco, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-xl" data-testid="arco-detail-dialog">
        <DialogHeader>
          <DialogTitle>Detalle ARCO: {arco.arco_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-slate-500">Tipo:</span> <span className="text-white ml-2">{arcoTypeLabels[arco.request_type]}</span></div>
            <div><span className="text-slate-500">Estado:</span> <Badge className={statusColors[arco.status] + " ml-2"}>{arco.status}</Badge></div>
            <div><span className="text-slate-500">Titular:</span> <span className="text-white ml-2">{arco.data_subject_name}</span></div>
            <div><span className="text-slate-500">Email:</span> <span className="text-white ml-2">{arco.data_subject_email || "N/A"}</span></div>
          </div>
          <div>
            <span className="text-slate-500">Descripcion:</span>
            <p className="text-slate-300 mt-1">{arco.description || "Sin descripcion"}</p>
          </div>
          {arco.resolution && (
            <div>
              <span className="text-slate-500">Resolucion:</span>
              <p className="text-slate-300 mt-1">{arco.resolution}</p>
            </div>
          )}
          <div>
            <span className="text-slate-500 block mb-2">Historial:</span>
            <div className="space-y-2 pl-3 border-l-2 border-slate-700">
              {(arco.timeline || []).map((t, i) => (
                <div key={i} className="text-xs">
                  <Badge className={statusColors[t.status] + " mb-1"}>{t.status}</Badge>
                  <p className="text-slate-400">{t.notes || "—"} | {new Date(t.timestamp).toLocaleString("es-MX")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusUpdateDialog({ arco, onClose, onUpdated }) {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!status) { toast.error("Selecciona un estado"); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API}/privacy/arco/${arco.arco_id}/status`, { status, notes }, { withCredentials: true });
      toast.success("Estado actualizado");
      onClose();
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="status-update-dialog">
        <DialogHeader>
          <DialogTitle>Actualizar Estado — {arco.arco_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nuevo Estado</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="status-select">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="in_review">En Revision</SelectItem>
                <SelectItem value="approved">Aprobado</SelectItem>
                <SelectItem value="denied">Denegado</SelectItem>
                <SelectItem value="completed">Completado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notas / Resolucion</Label>
            <Textarea className="bg-slate-800 border-slate-700" rows={3} value={notes}
              onChange={(e) => setNotes(e.target.value)} data-testid="status-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="status-submit-btn">
            {submitting ? "Actualizando..." : "Actualizar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
